'use client';

import { IMyAccountLogin } from '@/.generated';
import { Col } from '@/components/primitives/Grid';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { trackAdobeClick } from '@/utils/adobe';
import { getCSLPAttributes } from '@/utils/type-guards';
import Link from 'next/link';
import { defaultVariants } from './BannerLogin.styles';
import { buttonVariants } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper.styles';
import { useAccountLogin } from '@/lib/hooks/useAccountLogin';
import { WithMetadata } from 'typescript';
import { IBaseComponentProps } from '@/lib/types';
import { toPascalCase } from '@/utils/string-utils';
import { MY_ACCOUNT_LOGIN_URL } from '@/utils/Constants';

type BannerLoginProps = IMyAccountLogin &
  WithMetadata<IBaseComponentProps> & {
    hasBannerCarousel?: boolean;
  };

const Default = (props: BannerLoginProps) => {
  const {
    login_form_label,
    logout_link,
    login_button,
    disclaimer_note,
    mobile_logged_in_content,
    username_field_placeholder,
    password_field_placeholder,
    forgot_password_link,
    sign_up_content,
    $,
  } = props;

  const {
    container,
    loginLabel,
    formWrapper,
    loginForm,
    formInput,
    formActions,
    forgotLink,
    loginDisclaimer,
    loginSignUp,
    logoutButton,
    loginSubmit,
  } = defaultVariants();

  const loginSubmitClass = buttonVariants({
    variant: login_button?.cta_variant ?? 'primary',
    size: 'variable',
    className: loginSubmit(),
  });

  const userLoggedIn = useAccountLogin();

  return (
    <Col ml={props.hasBannerCarousel ? 4 : 12} className={container()}>
      {!userLoggedIn && (
        <PlainTextWrapper
          content={login_form_label}
          cslpAttribute={$?.login_form_label}
          className={loginLabel()}
        />
      )}
      <div className={formWrapper()}>
        {!userLoggedIn && (
          <>
            <form
              // data-key="loginForm"
              id="loginForm"
              method="post"
              action={login_button?.login_link?.href}
              className={loginForm()}
              onSubmit={() =>
                trackAdobeClick({
                  clickName: login_button?.login_link?.title ?? 'Login',
                  clickType: 'button',
                  clickLocation: 'stickyTileLogin',
                  clickUrl: MY_ACCOUNT_LOGIN_URL,
                })
              }
            >
              <input
                type="text"
                id="UserName"
                name="UserName"
                autoComplete="username"
                placeholder={username_field_placeholder}
                className={formInput()}
                {...getCSLPAttributes($?.username_field_placeholder)}
              />
              <input
                type="password"
                id="Password"
                name="Password"
                autoComplete="current-password"
                placeholder={password_field_placeholder}
                className={formInput()}
                {...getCSLPAttributes($?.password_field_placeholder)}
              />
              <div className={formActions()}>
                {forgot_password_link?.href && (
                  <Link
                    href={forgot_password_link.href}
                    className={forgotLink()}
                    onClick={() =>
                      trackAdobeClick({
                        clickName: forgot_password_link?.title ?? 'Forgot Password',
                        clickType: 'link',
                        clickLocation: 'stickyTileLogin',
                      })
                    }
                    {...getCSLPAttributes($?.forgot_password_link)}
                  >
                    <PlainTextWrapper content={forgot_password_link.title} />
                  </Link>
                )}
                <input
                  type="submit"
                  id="loginSubmit"
                  value={login_button?.login_link?.title}
                  className={loginSubmitClass}
                  {...getCSLPAttributes($?.login_link)}
                />
              </div>
            </form>
            <RichTextWrapper
              content={disclaimer_note}
              className={loginDisclaimer()}
              cslpAttribute={$?.disclaimer_note}
            />
            <RichTextWrapper
              content={sign_up_content}
              className={loginSignUp()}
              cslpAttribute={$?.sign_up_content}
            />
          </>
        )}
        {userLoggedIn && (
          <>
            <RichTextWrapper
              content={mobile_logged_in_content}
              cslpAttribute={$?.mobile_logged_in_content}
              parentClassName="bannerLoggedInContent"
            />
            <Link
              href={logout_link?.href || ''}
              className={logoutButton()}
              onClick={() =>
                trackAdobeClick({
                  clickName: logout_link?.title ?? 'Logout',
                  clickType: 'link',
                  clickLocation: 'mobileLogin',
                })
              }
              {...getCSLPAttributes($?.logout_link)}
            >
              {logout_link?.title}
            </Link>
          </>
        )}
      </div>
    </Col>
  );
};

const variants = {
  Default,
};

export const BannerLogin = (props: BannerLoginProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
